// ─────────────────────────────────────────────
// i18n — Simple translation utility
//
// Uses the locale store to pick the current language,
// then resolves dot-notation keys from locale JSON files.
// ─────────────────────────────────────────────

import en from "@/locales/en.json";
import sr from "@/locales/sr.json";
import type { Language } from "@/types";

type LocaleData = typeof en;

const locales: Record<Language, LocaleData> = {
  en,
  sr: sr as LocaleData,
};

/** Values usable for `{placeholder}` interpolation; `count` also selects the plural form. */
export type TranslateParams = Record<string, string | number>;

function resolve(root: unknown, parts: string[]): unknown {
  let current: unknown = root;
  for (const part of parts) {
    if (current && typeof current === "object" && part in current) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return current;
}

/**
 * CLDR cardinal plural category (integer rules, no ICU dependency).
 * en: one/other · sr: one/few/other.
 */
function pluralCategory(lang: Language, n: number): "one" | "few" | "other" {
  const abs = Math.abs(n);
  if (lang === "sr") {
    const mod10 = abs % 10;
    const mod100 = abs % 100;
    if (mod10 === 1 && mod100 !== 11) return "one";
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "few";
    return "other";
  }
  return abs === 1 ? "one" : "other";
}

/** Pick a plural leaf from a `{one, few, other}` locale node. */
function pickPlural(node: Record<string, unknown>, lang: Language, count: number): unknown {
  return node[pluralCategory(lang, count)] ?? node.other ?? node.one;
}

function interpolate(text: string, params?: TranslateParams): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match
  );
}

/**
 * Resolve a dot-notation translation key.
 * Falls back to English if key is missing in current locale.
 * Falls back to the key string itself if missing everywhere.
 *
 * Leaves may be plain strings or plural objects (`{one, few, other}` —
 * selected via `params.count`). `{name}` placeholders are interpolated
 * from `params`.
 *
 * @example t("nav.dashboard", "en") // → "Dashboard"
 * @example t("nav.dashboard", "sr") // → "Kontrolna tabla"
 * @example t("pagination.results", "sr", { count: 3 }) // → "3 rezultata"
 */
export function t(key: string, lang: Language = "en", params?: TranslateParams): string {
  const parts = key.split(".");

  for (const source of lang === "en" ? [locales.en] : [locales[lang] ?? locales.en, locales.en]) {
    let node = resolve(source, parts);
    if (node && typeof node === "object" && typeof params?.count === "number") {
      node = pickPlural(node as Record<string, unknown>, lang, params.count);
    }
    if (typeof node === "string") return interpolate(node, params);
  }

  // Final fallback: return the key itself
  return key;
}

/**
 * Create a bound translator for a given language.
 * Useful when you have the language available as a variable.
 */
export function createTranslator(lang: Language) {
  return (key: string, params?: TranslateParams) => t(key, lang, params);
}
